import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Checkbox,
  FormControl,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';

import {
  ActionItem,
  BuildingWorkflowResponse,
  LeaseLifecycle,
  Process,
} from '@urbanmix-tech/shared-js';

import type { RootState } from '@/store';
import { useSelector } from 'react-redux';

import WorkflowsProvider from '@/components/BuildingWorkflows/WorkflowsProvider';
import { useLoading } from '@/shared/context/UIContextProvider';
import { useAlert } from '@/contexts/Alert/AlertContext';
import { convertPseudoDateToRealDate } from '@/helpers/common';
import { useProcessSettings } from '@/shared/context/ProcessSettingsProvider';

import { ActionGroupList } from './TrackerActionGroupList';
import { WithId } from '../BuildingWorkflows/BuildingWorkflows';
import { TrackerProcessGroup } from './TrackerProcessGroup';

// Status priority for sorting: lower = higher priority
const PROCESS_STATUS_PRIORITY: Record<string, number> = {
  start: 0,
  open: 1,
  paused: 2,
  completed: 3,
  cancelled: 4,
};

const STORAGE_KEY = (unitId: string) => `tasks-tab-filters-${unitId}`;

type FilterState = {
  sortOrder: string;
  timeSpan: string;
  selectedStatus: string[];
  selectedProcessTypes: string[];
  selectedProcessStatus: string[];
  selectedOwners: string[];
};

const DEFAULT_FILTERS: FilterState = {
  sortOrder: 'createdAt',
  timeSpan: 'all',
  selectedStatus: [],
  selectedProcessTypes: [],
  selectedProcessStatus: [],
  selectedOwners: [],
};

function loadFilters(unitId: string): FilterState {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY(unitId));
    if (raw) return { ...DEFAULT_FILTERS, ...JSON.parse(raw) };
  } catch {}
  return DEFAULT_FILTERS;
}

function saveFilters(unitId: string, filters: FilterState) {
  try {
    sessionStorage.setItem(STORAGE_KEY(unitId), JSON.stringify(filters));
  } catch {}
}

type TasksTabContentProps = {
  projectId: string;
  unitId: string;
  actionItems: ActionItem[];
  leaseStatusFilter: LeaseLifecycle[];
  onClickActionItem(actionItem: ActionItem): void;
  handleOpenActionItemById(obj: WithId): void;
  onBuildingDataChange?: (data: Record<string, any>) => void;
};

export function TasksTabContent({
  projectId,
  unitId,
  actionItems,
  onClickActionItem,
  handleOpenActionItemById,
  onBuildingDataChange,
}: TasksTabContentProps) {
  const selectedUnit = useSelector(
    (state: RootState) => state.project.selectedUnit
  );
  const processes = useSelector((state: RootState) => state.project.processes);
  const { getProcessTypeConfig } = useProcessSettings();

  // Load persisted filter state for this unit
  const [filters, setFilters] = useState<FilterState>(() => loadFilters(unitId));

  // Reset filters when switching units
  useEffect(() => {
    setFilters(loadFilters(unitId));
  }, [unitId]);

  // Persist filter changes to sessionStorage
  useEffect(() => {
    saveFilters(unitId, filters);
  }, [unitId, filters]);

  const { sortOrder, timeSpan, selectedStatus, selectedProcessTypes, selectedProcessStatus, selectedOwners } = filters;

  const set = <K extends keyof FilterState>(key: K, value: FilterState[K]) =>
    setFilters((prev) => ({ ...prev, [key]: value }));

  const { createLoading } = useLoading();
  const loader = useMemo(() => createLoading(), []);
  const { showAlert } = useAlert();

  // All unit processes (unfiltered, for deriving filter options)
  const allUnitProcesses = useMemo(
    () => processes.filter((p: Process) => p.unitId === unitId),
    [processes, unitId]
  );

  // Derive available filter options from this unit's data
  const { statuses, processTypes, processStatuses, owners } = useMemo(() => {
    const unitActionItems = actionItems.filter((a) => a.unitId === unitId);
    const statusSet = new Set<string>();
    const processTypeSet = new Set<string>();
    const processStatusSet = new Set<string>();
    const ownerSet = new Set<string>();

    unitActionItems.forEach((item) => {
      if (item.status && item.status !== 'ignored') statusSet.add(item.status);
      const proc = allUnitProcesses.find((p: Process) => p.id === item.processId);
      if (proc?.type) processTypeSet.add(proc.type);
      if (proc?.status) processStatusSet.add(proc.status);
      if (item.owner?.email) ownerSet.add(item.owner.email);
    });
    allUnitProcesses.forEach((p: Process) => {
      if (p.type) processTypeSet.add(p.type);
      if (p.status) processStatusSet.add(p.status);
    });

    return {
      statuses: Array.from(statusSet).sort(),
      processTypes: Array.from(processTypeSet).sort(),
      processStatuses: Array.from(processStatusSet).sort(),
      owners: Array.from(ownerSet).sort(),
    };
  }, [actionItems, allUnitProcesses, unitId]);

  // Filtered + sorted processes
  const unitProcesses = useMemo(() => {
    return allUnitProcesses
      .filter((p: Process) => {
        if (selectedProcessTypes.length > 0 && !selectedProcessTypes.includes(p.type || '')) return false;
        if (selectedProcessStatus.length > 0) {
          if (!selectedProcessStatus.includes(p.status || '')) return false;
        } else {
          // Default: hide cancelled
          if (p.status === 'cancelled') return false;
        }
        return true;
      })
      .sort((a, b) => {
        const aPriority = PROCESS_STATUS_PRIORITY[a.status ?? ''] ?? 99;
        const bPriority = PROCESS_STATUS_PRIORITY[b.status ?? ''] ?? 99;
        if (aPriority !== bPriority) return aPriority - bPriority;
        // Within same status: most recently started first
        const aDate = convertPseudoDateToRealDate(a.startedAt as any)?.getTime() ?? 0;
        const bDate = convertPseudoDateToRealDate(b.startedAt as any)?.getTime() ?? 0;
        return bDate - aDate;
      });
  }, [allUnitProcesses, selectedProcessTypes, selectedProcessStatus]);

  // Filtered orphan action items (no processId)
  const filteredActionItems = useMemo(() => {
    const now = new Date().getTime();
    let cutoffDate: number | null = null;
    if (timeSpan === '7days') cutoffDate = now - 7 * 24 * 60 * 60 * 1000;
    else if (timeSpan === '30days') cutoffDate = now - 30 * 24 * 60 * 60 * 1000;
    else if (timeSpan === '90days') cutoffDate = now - 90 * 24 * 60 * 60 * 1000;

    return actionItems.filter((item) => {
      if (item.unitId !== selectedUnit?.apartmentId) return false;
      if (item.processId) return false;
      if (item.status === 'ignored') return false;
      if (cutoffDate !== null) {
        const d = convertPseudoDateToRealDate(item.createDate);
        if (!d || d.getTime() < cutoffDate) return false;
      }
      if (selectedStatus.length > 0 && !selectedStatus.includes(item.status || '')) return false;
      if (selectedOwners.length > 0 && (!item.owner?.email || !selectedOwners.includes(item.owner.email))) return false;
      return true;
    });
  }, [actionItems, selectedUnit, timeSpan, selectedStatus, selectedOwners]);

  const hasCancelledProcesses = allUnitProcesses.some((p: Process) => p.status === 'cancelled');

  return (
    <div className="flex w-full flex-col gap-22 overflow-auto p-[15px]">
      <Stack className="flex-1 pr-[8px]" gap="16px">
        {/* Filter row */}
        <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
          {/* Sort */}
          <FormControl size="small" variant="standard" sx={{ minWidth: 140 }}>
            <Select value={sortOrder} onChange={(e) => set('sortOrder', e.target.value)} displayEmpty>
              <MenuItem value="createdAt">Sort by Newest</MenuItem>
              <MenuItem value="dueDate">Sort by Due Date</MenuItem>
            </Select>
          </FormControl>

          {/* Time Span */}
          <FormControl size="small" variant="standard" sx={{ minWidth: 100 }}>
            <Select value={timeSpan} onChange={(e) => set('timeSpan', e.target.value)} displayEmpty>
              <MenuItem value="all">All Time</MenuItem>
              <MenuItem value="7days">Last 7 Days</MenuItem>
              <MenuItem value="30days">Last 30 Days</MenuItem>
              <MenuItem value="90days">Last 90 Days</MenuItem>
            </Select>
          </FormControl>

          {/* Task Status */}
          {statuses.length > 0 && (
            <FormControl size="small" variant="standard" sx={{ minWidth: 100 }}>
              <Select
                multiple
                value={selectedStatus}
                onChange={(e) => set('selectedStatus', e.target.value as string[])}
                displayEmpty
                renderValue={(selected) =>
                  selected.length === 0 ? 'Task Status' :
                  selected.length === 1 ? selected[0] :
                  `Task Status (${selected.length})`
                }
              >
                {statuses.map((status) => (
                  <MenuItem key={status} value={status}>
                    <Checkbox checked={selectedStatus.includes(status)} size="small" />
                    <Typography variant="body2">{status}</Typography>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Process Type */}
          {processTypes.length > 0 && (
            <FormControl size="small" variant="standard" sx={{ minWidth: 120 }}>
              <Select
                multiple
                value={selectedProcessTypes}
                onChange={(e) => set('selectedProcessTypes', e.target.value as string[])}
                displayEmpty
                renderValue={(selected) => {
                  if (selected.length === 0) return 'Process Type';
                  if (selected.length === 1) {
                    const config = getProcessTypeConfig(selected[0]);
                    return (
                      <Stack direction="row" alignItems="center" gap={0.5}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '2px', backgroundColor: config.color, flexShrink: 0 }} />
                        <Typography variant="body2">{config.displayName}</Typography>
                      </Stack>
                    );
                  }
                  return `Process Type (${selected.length})`;
                }}
              >
                {processTypes.map((type) => {
                  const config = getProcessTypeConfig(type);
                  return (
                    <MenuItem key={type} value={type}>
                      <Checkbox checked={selectedProcessTypes.includes(type)} size="small" />
                      <Box sx={{ width: 8, height: 8, borderRadius: '2px', backgroundColor: config.color, flexShrink: 0, mr: 1 }} />
                      <Typography variant="body2">{config.displayName}</Typography>
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          )}

          {/* Process Status */}
          {processStatuses.length > 0 && (
            <FormControl size="small" variant="standard" sx={{ minWidth: 130 }}>
              <Select
                multiple
                value={selectedProcessStatus}
                onChange={(e) => set('selectedProcessStatus', e.target.value as string[])}
                displayEmpty
                renderValue={(selected) =>
                  selected.length === 0
                    ? hasCancelledProcesses ? 'Excl. cancelled' : 'Process Status'
                    : selected.length === 1 ? selected[0]
                    : `Process Status (${selected.length})`
                }
              >
                {processStatuses.map((status) => (
                  <MenuItem key={status} value={status}>
                    <Checkbox checked={selectedProcessStatus.includes(status)} size="small" />
                    <Typography variant="body2">{status}</Typography>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Owner */}
          {owners.length > 0 && (
            <FormControl size="small" variant="standard" sx={{ minWidth: 120 }}>
              <Select
                multiple
                value={selectedOwners}
                onChange={(e) => set('selectedOwners', e.target.value as string[])}
                displayEmpty
                renderValue={(selected) =>
                  selected.length === 0 ? 'Owner' :
                  selected.length === 1 ? selected[0].split('@')[0] :
                  `Owner (${selected.length})`
                }
              >
                {owners.map((owner) => (
                  <MenuItem key={owner} value={owner}>
                    <Checkbox checked={selectedOwners.includes(owner)} size="small" />
                    <Typography variant="body2">{owner}</Typography>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Stack>

        <div className="flex flex-col h-full">
          {filteredActionItems.length > 0 && (
            <div className="flex-0">
              <ActionGroupList
                actionItems={filteredActionItems}
                onClickActionItem={onClickActionItem}
                onBuildingDataChange={onBuildingDataChange}
                sortOrder={sortOrder}
              />
            </div>
          )}

          {unitProcesses.length > 0 && (
            <div className="flex-1">
              <Stack gap="16px">
                {unitProcesses.map((process) => (
                  <TrackerProcessGroup
                    process={process}
                    key={process.id}
                    actionItems={actionItems.filter((ai) => ai.processId === process.id)}
                    onClickActionItem={onClickActionItem}
                    unitId={unitId}
                    category=""
                    editMode={false}
                  />
                ))}
              </Stack>
            </div>
          )}

          {projectId && (
            <Stack gap="16px" className="px-16 py-16">
              <Typography>Actions and Workflows</Typography>
              <hr />
              <WorkflowsProvider
                projectId={projectId}
                unitId={unitId}
                onWorkflowStart={(workflow) => {
                  loader.startLoading({ title: `Running ${workflow.name}` });
                }}
                onWorkflowEnd={(_, response: BuildingWorkflowResponse) => {
                  loader.stopLoading();
                  if (response.objects) {
                    response.objects.forEach((obj) => {
                      if (obj.type === 'task' && obj.id) {
                        handleOpenActionItemById(obj as WithId);
                      }
                    });
                  }
                  if (response.message) {
                    showAlert(response.message, 'success');
                  }
                }}
              />
            </Stack>
          )}
        </div>
      </Stack>
    </div>
  );
}
